import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../../../config/firebase';
import { useProfile } from '@shared/hooks/useProfile';

export const useUnreadCount = (conversationId?: string) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const { user } = useProfile();

  useEffect(() => {
    if (!user?.id) return;

    if (conversationId) {
      // For specific conversation
      const unreadQuery = query(
        collection(db, 'conversations', conversationId, 'messages'),
        where('read', '==', false),
        where('senderId', '!=', user.id)
      );

      const unsubscribe = onSnapshot(unreadQuery, (snapshot) => {
        setUnreadCount(snapshot.size);
      });

      return () => unsubscribe();
    } else {
      // For global unread count, first get user's conversations
      const messageUnsubscribes: (() => void)[] = [];

      const conversationsQuery = query(
        collection(db, 'conversations'),
        where('participants', 'array-contains', user.id)
      );

      const conversationUnsubscribe = onSnapshot(conversationsQuery, (snapshot) => {
        // Clean up previous message listeners when conversations change
        messageUnsubscribes.forEach(unsubscribe => unsubscribe());
        messageUnsubscribes.length = 0;

        if (snapshot.empty) {
          setUnreadCount(0);
          return;
        }

        // For each conversation, get unread messages count
        let totalUnread = 0;

        snapshot.docs.forEach((conversationDoc) => {
          const messagesQuery = query(
            collection(db, 'conversations', conversationDoc.id, 'messages'),
            where('read', '==', false),
            where('senderId', '!=', user.id)
          );

          const messageUnsubscribe = onSnapshot(messagesQuery, (messageSnapshot) => {
            totalUnread = totalUnread - (messageUnsubscribes[conversationDoc.id] || 0) + messageSnapshot.size;
            messageUnsubscribes[conversationDoc.id] = messageSnapshot.size;
            setUnreadCount(totalUnread);
          });

          messageUnsubscribes.push(messageUnsubscribe);
        });
      });

      // Cleanup function
      return () => {
        conversationUnsubscribe();
        messageUnsubscribes.forEach(unsubscribe => unsubscribe());
      };
    }
  }, [conversationId, user?.id]);

  return unreadCount;
};
